import { X } from 'lucide-react';
import { useEffect } from 'react';

import { API_PREFIX } from '@/lib/api/client';
import { pushOverlay } from '@/lib/overlayStack';
import { useT } from '@/lib/i18n';

const CROSS_ORIGIN = API_PREFIX.startsWith('http');

/**
 * A full-size image over everything else.
 *
 * Escape is captured rather than bubbled, so closing the preview does not also
 * close the task dialog underneath it.
 */
export function ImageLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  const t = useT();
  useEffect(() => {
    // Announce that something now sits above the dialog, so the dialog stops
    // answering Escape while this is open.
    const release = pushOverlay();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });

    return () => {
      release();
      document.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
      role="presentation"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t('attachment.closePreview')}
        className="absolute right-4 top-4 rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
      <img
        src={src}
        alt={alt}
        {...(CROSS_ORIGIN ? { crossOrigin: 'use-credentials' as const } : {})}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[88vh] max-w-full rounded object-contain"
      />
    </div>
  );
}
